import { useEffect, useState, type CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/services/api'
import { Modal } from '@/components/Modal'
import { CardSkeleton } from '@/components/SkeletonLoader'
import { useHasPermission } from '@/hooks/usePermission'

type FeatureValue = boolean | string | { pending: true; label: string }

type PricingTier = {
  pricing_tier_id: number
  slug: string
  name: string
  sort_order: number
  monthly_price: number
  annual_price: number | null
  booking_allowance: number | null
  booking_allowance_note: string | null
  booking_overage_per_unit: number | null
  ai_credits_included_usd: number
  ai_credits_are_trial: boolean
  cta_label: string
  feature_list: string[]
  highlight_badge: string | null
  blurb: string
  active: boolean
  fee_plan_id: number | null
  max_products: number
  max_bookings: number
  billing?: {
    monthly: { linked: boolean; stripe: 'ready' | 'pending' | 'not_required' }
    annual: { linked: boolean; stripe: 'ready' | 'pending' | 'not_required' } | null
  }
}

type FeatureRow = {
  feature_row_id: number
  feature: string
  sort_order: number
  values: Record<string, FeatureValue>
}

type Catalog = {
  tiers: PricingTier[]
  featureRows: FeatureRow[]
  settings: { annual_discount_percent: number; seeded: boolean }
}

type TierForm = {
  slug: string
  name: string
  sort_order: string
  monthly_price: string
  annual_price: string
  booking_allowance: string
  booking_allowance_note: string
  booking_overage_per_unit: string
  ai_credits_included_usd: string
  ai_credits_are_trial: boolean
  cta_label: string
  feature_list: string
  highlight_badge: string
  blurb: string
  active: boolean
  fee_plan_id: string
  max_products: string
  max_bookings: string
}

const emptyForm = (sortOrder: number): TierForm => ({
  slug: '',
  name: '',
  sort_order: String(sortOrder),
  monthly_price: '',
  annual_price: '',
  booking_allowance: '',
  booking_allowance_note: '',
  booking_overage_per_unit: '',
  ai_credits_included_usd: '0',
  ai_credits_are_trial: false,
  cta_label: '',
  feature_list: '',
  highlight_badge: '',
  blurb: '',
  active: true,
  fee_plan_id: '',
  max_products: '5',
  max_bookings: '-1',
})

const fieldStyle: CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  font: 'inherit',
  boxSizing: 'border-box',
}

const labelStyle: CSSProperties = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: '0.85rem',
}

function money(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

function slugify(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

function slugifyDraft(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-/, '')
}

function suggestedAnnual(monthly: string, discount: number): number | null {
  const price = Number(monthly)
  if (!monthly || Number.isNaN(price) || price === 0) return null
  return Math.round(price * (1 - discount / 100))
}

function errorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || fallback
}

function readCell(value: FeatureValue | undefined): { kind: 'yes' | 'no' | 'text' | 'pending'; text: string } {
  if (value === true) return { kind: 'yes', text: '' }
  if (value === false || value === undefined) return { kind: 'no', text: '' }
  if (typeof value === 'object') return { kind: 'pending', text: value.label }
  return { kind: 'text', text: value }
}

function writeCell(kind: 'yes' | 'no' | 'text' | 'pending', text: string): FeatureValue {
  if (kind === 'yes') return true
  if (kind === 'no') return false
  if (kind === 'pending') return { pending: true, label: text }
  return text
}

function formFromTier(tier: PricingTier): TierForm {
  return {
    slug: tier.slug,
    name: tier.name,
    sort_order: String(tier.sort_order),
    monthly_price: String(tier.monthly_price),
    annual_price: tier.annual_price == null ? '' : String(tier.annual_price),
    booking_allowance: tier.booking_allowance == null ? '' : String(tier.booking_allowance),
    booking_allowance_note: tier.booking_allowance_note || '',
    booking_overage_per_unit: tier.booking_overage_per_unit == null ? '' : String(tier.booking_overage_per_unit),
    ai_credits_included_usd: String(tier.ai_credits_included_usd),
    ai_credits_are_trial: tier.ai_credits_are_trial,
    cta_label: tier.cta_label,
    feature_list: tier.feature_list.join('\n'),
    highlight_badge: tier.highlight_badge || '',
    blurb: tier.blurb,
    active: tier.active,
    fee_plan_id: tier.fee_plan_id == null ? '' : String(tier.fee_plan_id),
    max_products: String(tier.max_products ?? 5),
    max_bookings: String(tier.max_bookings ?? -1),
  }
}

function payloadFromForm(form: TierForm) {
  return {
    slug: slugify(form.slug),
    name: form.name.trim(),
    sort_order: Number(form.sort_order),
    monthly_price: Number(form.monthly_price),
    annual_price: form.annual_price.trim() === '' ? null : Number(form.annual_price),
    booking_allowance: form.booking_allowance.trim() === '' ? null : Number(form.booking_allowance),
    booking_allowance_note: form.booking_allowance_note.trim() || null,
    booking_overage_per_unit: form.booking_overage_per_unit.trim() === '' ? null : Number(form.booking_overage_per_unit),
    ai_credits_included_usd: Number(form.ai_credits_included_usd || 0),
    ai_credits_are_trial: form.ai_credits_are_trial,
    cta_label: form.cta_label.trim(),
    feature_list: form.feature_list.split('\n').map((line) => line.trim()).filter(Boolean),
    highlight_badge: form.highlight_badge.trim() || null,
    blurb: form.blurb.trim(),
    active: form.active,
    fee_plan_id: form.fee_plan_id ? Number(form.fee_plan_id) : null,
    max_products: Number(form.max_products),
    max_bookings: Number(form.max_bookings),
  }
}

function billingSideLabel(
  label: string,
  side: { linked: boolean; stripe: 'ready' | 'pending' | 'not_required' } | null | undefined,
): string | null {
  if (!side) return null
  const link = side.linked ? `${label} plan linked` : `${label} plan not linked`
  const stripe = side.stripe === 'ready'
    ? 'Stripe price ready'
    : side.stripe === 'not_required'
      ? 'No Stripe charge'
      : 'Stripe price pending'
  return `${link} · ${stripe}`
}

export default function AdminPricingTiersPage() {
  const qc = useQueryClient()
  const canCreate = useHasPermission('plans:create')
  const canUpdate = useHasPermission('plans:update')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin-pricing-tiers'],
    queryFn: () => api.admin.pricingTiers.list(),
  })
  const { data: feePlansData } = useQuery({
    queryKey: ['admin-fee-plans'],
    queryFn: () => api.admin.feePlans.list(),
  })
  const feePlans = feePlansData?.data?.data?.plans || feePlansData?.data?.plans || []

  const catalog = (data?.data?.data || data?.data) as Catalog | undefined
  const tiers = catalog?.tiers || []

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<PricingTier | null>(null)
  const [form, setForm] = useState<TierForm>(emptyForm(0))
  const [formError, setFormError] = useState('')
  const [discount, setDiscount] = useState('15')
  const [rows, setRows] = useState<FeatureRow[]>([])
  const [hydrated, setHydrated] = useState(false)
  const [newFeature, setNewFeature] = useState('')
  const [pageError, setPageError] = useState('')

  useEffect(() => {
    if (!catalog || hydrated) return
    setRows(catalog.featureRows)
    setDiscount(String(catalog.settings.annual_discount_percent))
    setHydrated(true)
  }, [catalog, hydrated])

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['admin-pricing-tiers'] })
  }

  const saveTier = useMutation({
    mutationFn: (body: ReturnType<typeof payloadFromForm>) => (
      editing
        ? api.admin.pricingTiers.update(String(editing.pricing_tier_id), body)
        : api.admin.pricingTiers.create(body)
    ),
    onSuccess: async () => {
      setFormOpen(false)
      setEditing(null)
      setFormError('')
      await refresh()
    },
    onError: (err: any) => setFormError(errorMessage(err, 'Could not save this tier')),
  })

  const deactivateTier = useMutation({
    mutationFn: (tier: PricingTier) => api.admin.pricingTiers.update(String(tier.pricing_tier_id), { active: false }),
    onSuccess: refresh,
    onError: (err: any) => setPageError(errorMessage(err, 'Could not deactivate this tier')),
  })

  const saveDiscount = useMutation({
    mutationFn: (percent: number) => api.admin.pricingTiers.updateSettings(percent),
    onSuccess: refresh,
    onError: (err: any) => setPageError(errorMessage(err, 'Could not save the annual discount')),
  })

  const saveRows = useMutation({
    mutationFn: (next: FeatureRow[]) => api.admin.pricingTiers.updateFeatureRows(next.map((row, index) => ({
      feature_row_id: row.feature_row_id,
      feature: row.feature,
      sort_order: index,
      values: Object.fromEntries(tiers.map((tier) => [tier.slug, row.values[tier.slug] ?? false])),
    }))),
    onSuccess: async (response) => {
      const saved = response?.data?.data?.rows as FeatureRow[] | undefined
      if (saved) setRows(saved)
      await refresh()
    },
    onError: (err: any) => setPageError(errorMessage(err, 'Could not save the comparison')),
  })

  const addRow = useMutation({
    mutationFn: (feature: string) => api.admin.pricingTiers.createFeatureRow({
      feature,
      sort_order: rows.length,
      values: Object.fromEntries(tiers.map((tier) => [tier.slug, false])),
    }),
    onSuccess: (response) => {
      const row = response?.data?.data?.row as FeatureRow | undefined
      if (row) setRows((current) => [...current, row])
      setNewFeature('')
    },
    onError: (err: any) => setPageError(errorMessage(err, 'Could not add that feature')),
  })

  const deleteRow = useMutation({
    mutationFn: (rowId: number) => api.admin.pricingTiers.deleteFeatureRow(String(rowId)),
    onSuccess: (_response, rowId) => setRows((current) => current.filter((row) => row.feature_row_id !== rowId)),
    onError: (err: any) => setPageError(errorMessage(err, 'Could not remove that feature')),
  })

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm(tiers.length))
    setFormError('')
    setFormOpen(true)
  }

  const openEdit = (tier: PricingTier) => {
    setEditing(tier)
    setForm(formFromTier(tier))
    setFormError('')
    setFormOpen(true)
  }

  const submitTier = () => {
    const body = payloadFromForm(form)
    if (!body.name || !body.slug || !body.cta_label || !body.blurb) {
      setFormError('Name, slug, button label, and summary are required.')
      return
    }
    if (Number.isNaN(body.monthly_price) || Number.isNaN(body.sort_order)) {
      setFormError('Price and sort order need to be numbers.')
      return
    }
    saveTier.mutate(body)
  }

  const updateCell = (rowId: number, slug: string, kind: 'yes' | 'no' | 'text' | 'pending', text: string) => {
    setRows((current) => current.map((row) => (
      row.feature_row_id === rowId
        ? { ...row, values: { ...row.values, [slug]: writeCell(kind, text) } }
        : row
    )))
  }

  if (isLoading) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Pricing Tiers</h2>
        <CardSkeleton />
      </div>
    )
  }

  if (isError || !catalog) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Pricing Tiers</h2>
        <p style={{ color: '#dc3545' }}>{errorMessage(error, 'Pricing tiers could not be loaded. Run the latest database migration, then try again.')}</p>
      </div>
    )
  }

  const annualHint = suggestedAnnual(form.monthly_price, Number(discount) || 0)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Pricing Tiers</h2>
          <p style={{ margin: '0.35rem 0 0', color: '#666', maxWidth: '46rem', lineHeight: 1.45 }}>
            {tiers.length > 0
              ? 'This catalogue is what the marketing site and the vendor subscription page show. Saving a tier updates the billing plan behind it.'
              : 'No tiers yet. After the database migration, the first visit loads Free, Growth, Pro, Scale, Volume, and Infrastructure from the marketing pricing page.'}
          </p>
        </div>
        {canCreate && <button onClick={openCreate}>Create tier</button>}
      </div>

      {pageError && <p style={{ color: '#dc3545' }}>{pageError}</p>}

      <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>Annual discount</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <input
              type="number"
              min={0}
              max={100}
              value={discount}
              onChange={(event) => setDiscount(event.target.value)}
              disabled={!canUpdate}
              style={{ ...fieldStyle, width: '5.5rem' }}
            />
            <span>%</span>
          </div>
        </div>
        {canUpdate && (
          <button
            onClick={() => saveDiscount.mutate(Number(discount))}
            disabled={saveDiscount.isPending || Number(discount) === catalog.settings.annual_discount_percent}
          >
            {saveDiscount.isPending ? 'Saving...' : 'Save discount'}
          </button>
        )}
        <p style={{ margin: 0, color: '#888', fontSize: '0.8rem' }}>
          Suggested annual price is the monthly price after this discount, rounded to the nearest dollar. Free stays monthly only.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {tiers.map((tier) => (
          <article key={tier.pricing_tier_id} className="card" style={{ display: 'flex', flexDirection: 'column', padding: '1.25rem', opacity: tier.active ? 1 : 0.65 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
              <h3 style={{ margin: 0 }}>{tier.name}</h3>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: tier.active ? '#1a2e44' : '#666' }}>
                {tier.highlight_badge || (tier.active ? 'Active' : 'Inactive')}
              </span>
            </div>
            <p style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0.75rem 0 0.25rem' }}>
              ${money(tier.monthly_price)}<span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#666' }}>/mo</span>
            </p>
            <p style={{ margin: 0, color: '#666', fontSize: '0.8rem' }}>
              {tier.annual_price == null ? 'No annual price' : `$${money(tier.annual_price)}/mo billed annually`}
            </p>
            <p style={{ color: '#444', fontSize: '0.9rem' }}>{tier.blurb}</p>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#333' }}>
              {tier.booking_allowance == null ? 'No booking cap' : `${tier.booking_allowance.toLocaleString('en-US')} bookings/mo`}
              {tier.booking_overage_per_unit != null ? `, then $${money(tier.booking_overage_per_unit)}/booking` : ''}
              <br />
              ${money(tier.ai_credits_included_usd)} AI credits{tier.ai_credits_are_trial ? ' (14-day trial)' : '/mo'}
            </p>
            {tier.booking_allowance_note && (
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#888' }}>{tier.booking_allowance_note}</p>
            )}
            <ul style={{ margin: '0 0 1rem', paddingLeft: '1.1rem', color: '#444', fontSize: '0.85rem', flex: 1 }}>
              {tier.feature_list.map((feature) => <li key={feature} style={{ marginBottom: '0.35rem' }}>{feature}</li>)}
            </ul>
            <p style={{ margin: '0 0 0.35rem', fontSize: '0.75rem', color: '#888' }}>Button: {tier.cta_label} · {tier.slug}</p>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#666', lineHeight: 1.45 }}>
              {billingSideLabel('Monthly', tier.billing?.monthly) || 'Monthly plan not linked'}
              <br />
              {tier.billing?.annual == null
                ? 'No annual billing plan'
                : billingSideLabel('Annual', tier.billing.annual)}
              <br />
              Products: {tier.max_products === -1 ? 'Unlimited' : tier.max_products}
              {' · '}
              Bookings enforced: {tier.max_bookings === -1 ? 'Unlimited' : tier.max_bookings}
              {tier.fee_plan_id != null && (() => {
                const fee = feePlans.find((item: { fee_plan_id: number; name: string }) => item.fee_plan_id === tier.fee_plan_id)
                return fee ? ` · Fee: ${fee.name}` : ''
              })()}
            </p>
            {canUpdate && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => openEdit(tier)} style={{ fontSize: '0.8rem' }}>Edit</button>
                {tier.active && (
                  <button
                    onClick={() => {
                      if (confirm(`Deactivate ${tier.name}? It stays in the catalog and can be turned back on.`)) {
                        deactivateTier.mutate(tier)
                      }
                    }}
                    style={{ fontSize: '0.8rem', background: '#fee2e2', color: '#991b1b' }}
                  >
                    Deactivate
                  </button>
                )}
              </div>
            )}
          </article>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>Feature comparison</h3>
        {canUpdate && (
          <button onClick={() => saveRows.mutate(rows)} disabled={saveRows.isPending}>
            {saveRows.isPending ? 'Saving...' : 'Save comparison'}
          </button>
        )}
      </div>
      <p style={{ marginTop: 0, color: '#666', fontSize: '0.85rem' }}>
        Yes and No match the checkmarks on the pricing page. Use Text for amounts such as 1,500 or $0.25/booking, and Pending when a feature is announced but not live.
      </p>

      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem', minWidth: '16rem', position: 'sticky', left: 0, background: '#f8fafc' }}>Feature</th>
              {tiers.map((tier) => (
                <th key={tier.slug} style={{ padding: '0.75rem', minWidth: '9rem' }}>{tier.name}</th>
              ))}
              <th style={{ padding: '0.75rem' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.feature_row_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '0.5rem', position: 'sticky', left: 0, background: '#fff' }}>
                  <input
                    value={row.feature}
                    disabled={!canUpdate}
                    onChange={(event) => setRows((current) => current.map((item) => (
                      item.feature_row_id === row.feature_row_id ? { ...item, feature: event.target.value } : item
                    )))}
                    style={fieldStyle}
                  />
                </td>
                {tiers.map((tier) => {
                  const cell = readCell(row.values[tier.slug])
                  return (
                    <td key={tier.slug} style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                      <select
                        value={cell.kind}
                        disabled={!canUpdate}
                        onChange={(event) => updateCell(row.feature_row_id, tier.slug, event.target.value as 'yes' | 'no' | 'text' | 'pending', cell.text)}
                        style={{ ...fieldStyle, marginBottom: cell.kind === 'text' || cell.kind === 'pending' ? '0.35rem' : 0 }}
                      >
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="text">Text</option>
                        <option value="pending">Pending</option>
                      </select>
                      {(cell.kind === 'text' || cell.kind === 'pending') && (
                        <input
                          value={cell.text}
                          disabled={!canUpdate}
                          placeholder={cell.kind === 'pending' ? 'Shown when it goes live' : '1,500'}
                          onChange={(event) => updateCell(row.feature_row_id, tier.slug, cell.kind, event.target.value)}
                          style={fieldStyle}
                        />
                      )}
                    </td>
                  )
                })}
                <td style={{ padding: '0.5rem' }}>
                  {canUpdate && (
                    <button
                      onClick={() => {
                        if (confirm(`Remove "${row.feature}" from the comparison?`)) deleteRow.mutate(row.feature_row_id)
                      }}
                      style={{ fontSize: '0.75rem', background: '#fee2e2', color: '#991b1b' }}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canCreate && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', maxWidth: '32rem' }}>
          <input
            value={newFeature}
            onChange={(event) => setNewFeature(event.target.value)}
            placeholder="New comparison feature"
            style={fieldStyle}
          />
          <button
            onClick={() => {
              const feature = newFeature.trim()
              if (feature) addRow.mutate(feature)
            }}
            disabled={addRow.isPending || newFeature.trim().length < 2}
          >
            Add
          </button>
        </div>
      )}

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Edit ${editing.name}` : 'New pricing tier'} maxWidth="760px">
        {formError && <div style={{ color: '#dc3545', marginBottom: '1rem' }}>{formError}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '0.85rem' }}>
          <div>
            <label style={labelStyle}>Name</label>
            <input
              value={form.name}
              onChange={(event) => {
                const name = event.target.value
                setForm((current) => ({
                  ...current,
                  name,
                  slug: editing ? current.slug : slugify(name),
                }))
              }}
              style={fieldStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Slug</label>
            <input value={form.slug} onChange={(event) => setForm({ ...form, slug: slugifyDraft(event.target.value) })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Sort order</label>
            <input type="number" value={form.sort_order} onChange={(event) => setForm({ ...form, sort_order: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Monthly price (USD)</label>
            <input type="number" min={0} step="0.01" value={form.monthly_price} onChange={(event) => setForm({ ...form, monthly_price: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Annual price per month (USD)</label>
            <input type="number" min={0} step="0.01" value={form.annual_price} onChange={(event) => setForm({ ...form, annual_price: event.target.value })} placeholder="Blank = no annual plan" style={fieldStyle} />
            {annualHint != null && (
              <button
                type="button"
                onClick={() => setForm({ ...form, annual_price: String(annualHint) })}
                style={{ marginTop: '0.35rem', fontSize: '0.75rem', background: 'transparent', color: '#1a2e44', border: 'none', padding: 0, textDecoration: 'underline' }}
              >
                Use suggested ${money(annualHint)}
              </button>
            )}
          </div>
          <div>
            <label style={labelStyle}>Bookings shown on the pricing page</label>
            <input type="number" min={0} value={form.booking_allowance} onChange={(event) => setForm({ ...form, booking_allowance: event.target.value })} placeholder="Blank = no cap shown" style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Max products enforced (-1 = unlimited)</label>
            <input type="number" value={form.max_products} onChange={(event) => setForm({ ...form, max_products: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Max bookings enforced (-1 = unlimited)</label>
            <input type="number" value={form.max_bookings} onChange={(event) => setForm({ ...form, max_bookings: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Fee plan</label>
            <select value={form.fee_plan_id} onChange={(event) => setForm({ ...form, fee_plan_id: event.target.value })} style={fieldStyle}>
              <option value="">No fee plan (use default)</option>
              {feePlans.map((fee: { fee_plan_id: number; name: string; percentage_fee: number; fixed_fee: number; currency: string }) => (
                <option key={fee.fee_plan_id} value={fee.fee_plan_id}>
                  {fee.name} ({fee.percentage_fee}%{fee.fixed_fee > 0 ? ` + ${fee.currency} ${fee.fixed_fee}` : ''})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Booking note</label>
            <input value={form.booking_allowance_note} onChange={(event) => setForm({ ...form, booking_allowance_note: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Overage per booking (USD)</label>
            <input type="number" min={0} step="0.01" value={form.booking_overage_per_unit} onChange={(event) => setForm({ ...form, booking_overage_per_unit: event.target.value })} placeholder="Blank = no overage" style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>AI credits included (USD)</label>
            <input type="number" min={0} step="0.01" value={form.ai_credits_included_usd} onChange={(event) => setForm({ ...form, ai_credits_included_usd: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Button label</label>
            <input value={form.cta_label} onChange={(event) => setForm({ ...form, cta_label: event.target.value })} style={fieldStyle} />
          </div>
          <div>
            <label style={labelStyle}>Highlight badge</label>
            <input value={form.highlight_badge} onChange={(event) => setForm({ ...form, highlight_badge: event.target.value })} placeholder="Most popular" style={fieldStyle} />
          </div>
        </div>
        <div style={{ marginTop: '0.85rem' }}>
          <label style={labelStyle}>Summary</label>
          <input value={form.blurb} onChange={(event) => setForm({ ...form, blurb: event.target.value })} style={fieldStyle} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.85rem', fontSize: '0.85rem' }}>
          <input type="checkbox" checked={form.ai_credits_are_trial} onChange={(event) => setForm({ ...form, ai_credits_are_trial: event.target.checked })} />
          AI credits are a one-time trial, not a monthly allowance
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.85rem' }}>
          <input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
          Active
        </label>
        <div style={{ marginTop: '0.85rem' }}>
          <label style={labelStyle}>Features, one per line</label>
          <textarea
            value={form.feature_list}
            onChange={(event) => setForm({ ...form, feature_list: event.target.value })}
            rows={6}
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
          <button onClick={submitTier} disabled={saveTier.isPending}>
            {saveTier.isPending ? 'Saving...' : editing ? 'Update tier' : 'Create tier'}
          </button>
          <button onClick={() => setFormOpen(false)} style={{ background: '#e5e7eb', color: '#333' }}>Cancel</button>
        </div>
      </Modal>
    </div>
  )
}
